import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  whatsappGreetingQueryOptions,
} from "@/lib/whatsapp-greeting";
import {
  getMyAdminStatus,
  claimFirstAdmin,
  updateWhatsappGreeting,
} from "@/lib/admin-settings.functions";

export const Route = createFileRoute("/_authenticated/admin/settings")({
  component: AdminSettingsPage,
  head: () => ({
    meta: [
      { title: "Admin · Settings" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

function AdminSettingsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const updateFn = useServerFn(updateWhatsappGreeting);
  const claimFn = useServerFn(claimFirstAdmin);
  const statusFn = useServerFn(getMyAdminStatus);

  const greeting = useQuery(whatsappGreetingQueryOptions);
  const status = useQuery({
    queryKey: ["admin_status"],
    queryFn: () => statusFn(),
    staleTime: 0,
  });

  const [en, setEn] = useState("");
  const [pt, setPt] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    if (greeting.data) {
      setEn(greeting.data.en);
      setPt(greeting.data.pt);
    }
  }, [greeting.data]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setEmail(data.user?.email ?? null));
  }, []);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      await updateFn({ data: { en, pt } });
      await qc.invalidateQueries({ queryKey: ["app_settings", "whatsapp_greeting"] });
      setMsg({ kind: "ok", text: "Saved. New greeting is live across the site." });
    } catch (err) {
      setMsg({ kind: "err", text: err instanceof Error ? err.message : "Save failed" });
    } finally {
      setSaving(false);
    }
  }

  async function onClaim() {
    setMsg(null);
    try {
      await claimFn();
      await status.refetch();
      setMsg({ kind: "ok", text: "You are now admin." });
    } catch (err) {
      setMsg({ kind: "err", text: err instanceof Error ? err.message : "Failed" });
    }
  }

  async function onSignOut() {
    await qc.cancelQueries();
    qc.clear();
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  }

  const isAdmin = status.data?.isAdmin === true;
  const hasAnyAdmin = status.data?.hasAnyAdmin === true;

  return (
    <main className="min-h-screen bg-background">
      <header className="border-b border-emerald-deep/15 bg-emerald-deep text-[oklch(0.98_0.01_95)]">
        <div className="mx-auto max-w-4xl px-5 py-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="eyebrow text-gold">Morningstar · Admin</p>
            <h1 className="font-display text-2xl sm:text-3xl">Site settings</h1>
          </div>
          <div className="flex items-center gap-3 text-sm">
            {email && <span className="text-[oklch(0.92_0.02_95)]">{email}</span>}
            <Link to="/" className="text-gold hover:text-white transition-colors text-[11px] uppercase tracking-[0.22em] font-semibold">
              View site
            </Link>
            <button
              type="button"
              onClick={onSignOut}
              className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[oklch(0.92_0.02_95)] hover:text-gold transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-3xl px-5 py-10 space-y-8">
        {!isAdmin && !hasAnyAdmin && status.data && (
          <div className="border border-gold/40 bg-[oklch(0.97_0.04_85)] p-5">
            <h2 className="font-display text-xl text-emerald-deep">No admin yet</h2>
            <p className="mt-2 text-sm text-foreground/80">
              No user has admin role. Click below to claim it for this account.
            </p>
            <button
              type="button"
              onClick={onClaim}
              className="mt-4 inline-flex bg-emerald-deep px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-white hover:bg-emerald-deep/90 transition-colors"
            >
              Claim admin role
            </button>
          </div>
        )}

        {!isAdmin && hasAnyAdmin && status.data && (
          <div className="border border-red-300 bg-red-50 p-5">
            <h2 className="font-display text-xl text-red-900">Not authorised</h2>
            <p className="mt-2 text-sm text-red-900/80">
              An administrator already exists. Ask them to grant you admin access.
            </p>
          </div>
        )}

        <article className="border border-emerald-deep/15 bg-card p-6 sm:p-8 shadow-soft">
          <h2 className="font-display text-2xl text-emerald-deep">WhatsApp greeting</h2>
          <p className="mt-2 text-sm text-foreground/70">
            The first message a visitor sends when they click any WhatsApp button across the site
            (hero, cards, footer, landing pages, FAQ). Shown in the visitor's language.
          </p>

          <form onSubmit={onSave} className="mt-6 space-y-5">
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.22em] text-emerald-deep font-semibold">
                English
              </span>
              <textarea
                value={en}
                onChange={(e) => setEn(e.target.value)}
                disabled={!isAdmin || saving}
                rows={3}
                maxLength={500}
                required
                minLength={5}
                className="mt-2 w-full border border-emerald-deep/20 bg-white px-3 py-2.5 text-sm text-emerald-deep focus:outline-none focus:border-gold disabled:opacity-60"
              />
              <span className="mt-1 block text-xs text-foreground/50">{en.length}/500</span>
            </label>

            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.22em] text-emerald-deep font-semibold">
                Português
              </span>
              <textarea
                value={pt}
                onChange={(e) => setPt(e.target.value)}
                disabled={!isAdmin || saving}
                rows={3}
                maxLength={500}
                required
                minLength={5}
                className="mt-2 w-full border border-emerald-deep/20 bg-white px-3 py-2.5 text-sm text-emerald-deep focus:outline-none focus:border-gold disabled:opacity-60"
              />
              <span className="mt-1 block text-xs text-foreground/50">{pt.length}/500</span>
            </label>

            {msg && (
              <p className={`text-sm ${msg.kind === "ok" ? "text-emerald-deep" : "text-red-700"}`}>
                {msg.text}
              </p>
            )}

            <button
              type="submit"
              disabled={!isAdmin || saving}
              className="inline-flex items-center bg-gold px-7 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-deep hover:bg-[oklch(0.85_0.14_85)] transition-colors disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save greeting"}
            </button>
          </form>
        </article>
      </section>
    </main>
  );
}